<template>
  <div class="">
    <!-- Título principal -->
    <div class="my-6">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">
        Consultar Estado de Pago
      </h1>
    </div>

    <!-- Layout en dos columnas sin v-row -->
    <div class="grid grid-cols-2 xl:grid-cols-2 gap-6 xl:gap-8">
      <!-- Columna izquierda - Documentación -->
      <div class="space-y-4 xl:space-y-6">
        <!-- Información del endpoint -->
        <div class="bg-white rounded-lg border border-gray-200 p-4 xl:p-6">
          <h3 class="text-lg xl:text-xl font-semibold flex items-center">
            <span class="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm xl:text-base font-mono mr-3">POST</span>
            https://sb-api.wee.ar/v1/estados-pagos
          </h3>
        </div>

        <p>
          Para realizar la consulta de estado un pago o varios pagos.
        </p>
        <!-- Parámetros -->
        <h4 class="font-semibold mb-3 xl:mb-4 text-base xl:text-lg">
          Request
        </h4>

        <!-- Headers Table -->
        <DynamicTable title="Headers" :headers="headersEstadoPago" :data="headersData" height="auto" />

        <DynamicTable title="Body" :headers="bodyConfig" :data="bodyParameters" height="150px" />

        <h5 class="font-semibold mb-3 xl:mb-4 text-base xl:text-lg">
          Opciones en envío del objeto JSON
        </h5>
        <p>
          Cuando envía el objeto JSON puede:
        </p>
        <ul class="list-disc pl-4 mb-4 text-xs xl:text-base ">
          <li class="mb-2">
            Enviar solamente el <strong> "uuid"</strong> o el
            <strong> "external_reference"</strong> si desea consultar por el
            estado de un pago.
          </li>
          <li class="mb-2">
            Enviar un rango de fecha
            <strong>("fecha_desde", "fecha_hasta")</strong> si lo que desea es
            obtener el estado de los pagos realizados en ese rango de fecha. El
            rango de fecha no debe superar la cantidad de 7 días.
          </li>
          <li>
            Enviar un array de <strong>"uuids"</strong> si desea consultar por
            el estado de varios pagos.
          </li>
        </ul>
        <!-- Response -->
        <h4 class="font-semibold mb-3 xl:mb-4 text-base xl:text-lg">
          Response
        </h4>

        <DynamicTable title="Atributos del objeto de la respuesta." :headers="responseHeaders" :data="responseData"
          height="auto" />

        <DynamicTable title="Atributos del objeto de data." :headers="responseHeadersData" :data="responseObjectData"
          height="150px" />

        <DynamicTable title="Atributos del objeto pago_Intento." :headers="responseHeadersPagoIntento"
          :data="responseObjectPagoIntento" height="200px" />
      </div>

      <!-- Columna derecha - Ejemplos de código -->
      <div class="space-y-4 xl:space-y-6">
        <!-- Request Example -->
        <div class="bg-white rounded-lg border border-gray-200 p-3 xl:p-6">
          <h4 class="text-lg xl:text-xl font-semibold mb-3 flex items-center">
            Headers
          </h4>
          <span class="text-black">
            <p>
              <code class="text-black">Content-Type: application/json</code>
            </p>
            <p>
              <code class="text-black">apiKey: aa7943e1-a60c-99e7-8812-xxxxxxxxxxxx (
                <strong class="text-blue-500"
                  >mismo apiKey utilizado para la solicitud de pago</strong
                >).</code>
            </p>
          </span>
        </div>
        <CodeBlock title="Example Request: consultando por uuid" :code="requestExample" language="json" status="success"
          :is-large="true" :request="true" />
        <CodeBlock title="Example Request: consultando por Array de uuids " :code="requestExample2" language="json"
          status="success" :is-large="true" />
        <CodeBlock title="Example response" :code="responseExample" language="json" status="success" :is-large="true" />
      </div>
    </div>
  </div>
</template>
<script>
import DynamicTable from "../shared/DynamicTable.vue";
import CodeBlock from "../shared/CodeBlock.vue";

export default {
  name: "ConsultarEstadoPago",
  components: {
    DynamicTable,
    CodeBlock,
  },
  data() {
    return {
      // Configuración de headers para la tabla de Headers
      headersEstadoPago: [
        { key: "atributo", label: "atributo", isCode: true },
        { key: "tipo", label: "TIPO" },
        { key: "requerido", label: "REQUERIDO" },
        { key: "pattern", label: "PATTERN" },
        { key: "descripcion", label: "DESCRIPCIÓN" },
      ],

      // Datos de headers
      headersData: [
        {
          atributo: "apikey",
          tipo: "String",
          requerido: "Sí",
          pattern: "API Key",
          descripcion: "aa7943e1-a60c-99e7-8812-xxxxxxxxxxxx",
        },
        {
          atributo: "Content-Type",
          tipo: "String",
          requerido: "Sí",
          pattern: "application/json",
          descripcion:
            "El tipo MIME del contenido del cuerpo enviado. Por defecto JSON es Unicode UTF-8. No se debe configurar otro charset.",
        },
      ],

      // Configuración de headers para la tabla de Body
      bodyConfig: [
        { key: "atributo", label: "ATRIBUTO", isCode: true },
        { key: "tipo", label: "TIPO" },
        { key: "requerido", label: "REQUERIDO" },
        { key: "descripcion", label: "DESCRIPCIÓN" },
      ],

      // Datos de parámetros del body
      bodyParameters: [
        {
          atributo: "uuid",
          tipo: "String",
          requerido: "Sí",
          descripcion:
            "Es un valor recibido en la respuesta del pago realizado.",
        },
        {
          atributo: "external_reference",
          tipo: "String",
          requerido: "Sí",
          descripcion:
            "Es un valor enviado por el cliente al solicitar el pago.",
        },
        {
          atributo: "fecha_desde",
          tipo: "String",
          requerido: "Sí",
          descripcion: "DD-MM-AAAA",
        },
        {
          atributo: "fecha_hasta",
          tipo: "String",
          requerido: "Sí",
          descripcion: "DD-MM-AAAA",
        },
        {
          atributo: "uuids",
          tipo: "Array [String]",
          requerido: "Sí",
          descripcion: "Identificador de la operación.",
        },
      ],

      // Response

      responseHeaders: [
        { key: "atributo", label: "atributo", isCode: true },
        { key: "tipo", label: "TIPO" },
        { key: "descripcion", label: "DESCRIPCIÓN" },
      ],

      responseData: [
        {
          atributo: "data",
          tipo: "Array [Object]",
          descripcion: "Arreglo de objetos con los datos de la consulta.",
        },
        {
          atributo: "message",
          tipo: "Object",
          descripcion: "Mensaje de la consulta.",
        },
        {
          atributo: "status",
          tipo: "Boolean",
          descripcion: "Estado de la consulta.",
        },
        {
          atributo: "total",
          tipo: "Integer",
          descripcion: "Total de registros de la consulta.",
        },
      ],

      responseHeadersData: [
        { key: "atributo", label: "atributo", isCode: true },
        { key: "tipo", label: "TIPO" },
        { key: "descripcion", label: "DESCRIPCIÓN" },
      ],

      responseObjectData: [
        {
          atributo: "solicitudpago_id",
          tipo: "Integer",
          descripcion: "Identificador único de la solicitud de pago.",
        },
        {
          atributo: "uuid",
          tipo: "String",
          descripcion: "Identificador único de pago.",
        },
        {
          atributo: "payment_type",
          tipo: "String",
          descripcion: "Tipo de pago.",
        },
        {
          atributo: "pago_estado",
          tipo: "String",
          descripcion: "Estado del pago.",
        },
        {
          atributo: "estado",
          tipo: "Integer",
          descripcion: "Estado de la solicitud de pago.",
        },
        {
          atributo: "created_at",
          tipo: "DateTime",
          descripcion: "Fecha de creación de la solicitud de pago.",
        },
        {
          atributo: "external_reference",
          tipo: "Integer",
          descripcion:
            "Este atributo es utilizado como referencia para identificar la solicitud y sincronizar con tus sistemas de backend el origen de la operación.",
        },
        {
          atributo: "payer_name",
          tipo: "String",
          descripcion: "Nombre del pagador.",
        },
        {
          atributo: "payer_email",
          tipo: "String",
          descripcion: "Correo electrónico del pagador.",
        },
        {
          atributo: "description",
          tipo: "String",
          descripcion: "Descripción del estado de pago.",
        },
        {
          atributo: "first_due_date",
          tipo: "DateTime",
          descripcion: "Fecha de 1º Vencimiento. Formato dd/mm/aaaa.",
        },
        {
          atributo: "first_total",
          tipo: "Integer",
          descripcion: "Monto total antes de vencimiento.",
        },
        {
          atributo: "pago_Intento",
          tipo: "Array [Object]",
          descripcion: "Intentos de pago.",
        },
      ],

      responseHeadersPagoIntento: [
        { key: "atributo", label: "atributo", isCode: true },
        { key: "tipo", label: "TIPO" },
        { key: "descripcion", label: "DESCRIPCIÓN" },
      ],

      responseObjectPagoIntento: [
        {
          atributo: "pagointento_id",
          tipo: "Integer",
          descripcion: "Identificador único del intento de pago.",
        },
        {
          atributo: "channel",
          tipo: "String",
          descripcion: "Canal de pago.",
        },
        {
          atributo: "paid_at",
          tipo: "DateTime",
          descripcion: "Fecha de pago.",
        },
        {
          atributo: "is_available",
          tipo: "Boolean",
          descripcion: "Disponibilidad del pago.",
        },
        {
          atributo: "amount",
          tipo: "Integer",
          descripcion: "Monto total.",
        },
        {
          atributo: "GrossFee",
          tipo: "Float",
          descripcion:
            "Importe total correspondiente a la tarifa de procesamiento.",
        },
        {
          atributo: "net_fee",
          tipo: "Float",
          descripcion: "Comisión neta.",
        },
        {
          atributo: "fee_iva",
          tipo: "Float",
          descripcion: "IVA de la comisión.",
        },
        {
          atributo: "net_amount",
          tipo: "Float",
          descripcion: "Monto neto.",
        },
      ],

      // Ejemplos de código
      requestExample: `  {
      "uuid": "4fc0a8ee-6b85-482d-98a8-f267e5766bec"
      }`,
      requestExample2: `  {
      "uuids": ["b744718c-e46c-4838-8fcb-cc99057013af", "b6c50fad-7881-43ef-af5e-f829e551e8b3", "a972d1d8-8c1f-46d4-a254-5911123584c5"]
      }`,

      responseExample: `{
        "data": [
          {
            "solicitudpago_id": 1031,
            "uuid": "a972d1d8-8c1f-46d4-a254-5911123584c5",
            "payment_type": "INTERNET-WIFI",
            "pago_estado": "EXPIRED",
            "estado": "CADUCADO",
            "created_at": "2023-02-23 13:42:50 +0000 UTC",
            "external_reference": "2124",
            "payer_name": "VELAZCO IVAN",
            "payer_email": "velazcoivan2089@gmail.com",
            "description": "SERVICIOS DE INTERNET",
            "first_due_date": "2023-02-23 00:00:00 +0000 UTC",
            "first_total": 5000,
            "pago_Intento": [
                {
                  "pagointento_id": 619,
                  "channel": "OFFLINE",
                  "paid_at": "2023-02-23 13:43:18 +0000 UTC",
                  "is_available": false,
                  "amount": 5000,
                  "GrossFee": 0,
                  "net_fee": 0,
                  "fee_iva": 0,
                  "net_amount": 0
                }
              ]
            },
            {
              "solicitudpago_id": 1023,
              "uuid": "b6c50fad-7881-43ef-af5e-f829e551e8b3",
              "payment_type": "INTERNET-WIFI",
              "pago_estado": "ACCREDITED",
              "estado": "AUTORIZADO",
              "created_at": "2023-02-22 20:30:10 +0000 UTC",
              "external_reference": "2039",
              "payer_name": "LARRACET ERNESTO",
              "payer_email": "sin@correo.com",
              "description": "SERVICIOS DE INTERNET",
              "first_due_date": "2023-02-22 00:00:00 +0000 UTC",
              "first_total": 5000,
              "pago_Intento": [
                  {
                    "pagointento_id": 616,
                    "channel": "CREDIT",
                    "paid_at": "2023-02-22 17:37:00 +0000 UTC",
                    "is_available": true,
                    "amount": 5000,
                    "GrossFee": 96.8,
                    "net_fee": 80,
                    "fee_iva": 16.8,
                    "net_amount": 4903.2
                  }
                ]
              },
              {
                "solicitudpago_id": 961,
                "uuid": "b744718c-e46c-4838-8fcb-cc99057013af",
                "payment_type": "INTERNET-WIFI",
                "pago_estado": "ACCREDITED",
                "estado": "AUTORIZADO",
                "created_at": "2023-02-15 15:31:43 +0000 UTC",
                "external_reference": ""
                ,
                "payer_name": "CONSUMIDOR FINAL",
                "payer_email": ""
                ,
                "description": "SERVICIOS DE INTERNET",
                "first_due_date": "2023-02-15 00:00:00 +0000 UTC",
                "first_total": 5000,
                "pago_Intento": [
                    {
                      "pagointento_id": 592,
                      "channel": "CREDIT",
                      "paid_at": "2023-02-15 12:36:00 +0000 UTC",
                      "is_available": true,
                      "amount": 5000,
                      "GrossFee": 96.8,
                      "net_fee": 80,
                      "fee_iva": 16.8,
                      "net_amount": 4903.2
                    }
                  ]
                }
        ],
        "message": "datos enviados",
        "status": true,
        "total": 3
      }`,
    };
  },
};
</script>

<style scoped>
/* Responsive adjustments para el grid */
@media (max-width: 1279px) {
  .grid-cols-1.xl\:grid-cols-2 {
    grid-template-columns: 1fr;
  }
}
</style>
